import {
  TrajectoriesApi,
  TrajectoryService,
  type TrajectoryWellboreRef,
  type TrajectoryWidgetStateParams,
} from '@repo/go-widgets/trajectory';
import type {
  ReferenceDataTemplatesStore,
  ReferenceWellsChildrenStore,
  ReferenceWellsService,
  ReferenceWellsStore,
  TForcedPrimarySelection,
  TReferenceWellboreCatalogItem,
} from '@repo/reference-wells';
import { hasValue } from '@repo/utils';
import axios from 'axios';
import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';
import type { IReactionDisposer } from 'mobx';

import { dashboardServiceAgent } from 'src/api/dashboard-service-agent';
import { wellDirectoryServiceAgent } from 'src/api/well-directory-service-agent';
import type { TGroup, TWellListCombinedData } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import { requireService } from 'src/packages/di';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';

import type { TrajectoryWidgetEntity, TTrajectoryPlanOrFact } from './TrajectoryWidget.entity';

export type TTrajectoryReferenceWellsDeps = {
  referenceWellsService: ReferenceWellsService;
  referenceWellsStore: ReferenceWellsStore;
  referenceDataTemplatesStore: ReferenceDataTemplatesStore;
  referenceWellsChildrenStore: ReferenceWellsChildrenStore;
};

type TFetchedFactWellbore = Pick<TrajectoryWellboreRef, 'id' | 'parentWellboreId' | 'mdKickoff' | 'referencePoints'> & {
  id: number;
};

function pickActiveOrFirst<T extends { isActive: boolean }>(items: T[]): T | null {
  if (items.length === 0) {
    return null;
  }

  return items.find((item) => item.isActive) ?? items[0] ?? null;
}

export class TrajectoryWidgetStore implements IWidgetStore {
  readonly trajectoryService: TrajectoryService;
  readonly referenceWellsService: ReferenceWellsService;
  readonly referenceWellsStore: ReferenceWellsStore;
  readonly referenceDataTemplatesStore: ReferenceDataTemplatesStore;
  readonly referenceWellsChildrenStore: ReferenceWellsChildrenStore;

  @observable isGroupSelectOpen: boolean;
  @observable isReferenceWellsPanelOpen = false;

  private readonly entity: TrajectoryWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private effectDisposers: IReactionDisposer[] = [];
  private factWellboresAbortController: AbortController | null = null;

  @observable.ref private factWellboresCache: ReadonlyArray<TFetchedFactWellbore> = [];

  constructor(
    entity: TrajectoryWidgetEntity,
    groupSelectStore: GroupSelectStore,
    private readonly wellListPanel: TWellListCombinedData,
    referenceWells: TTrajectoryReferenceWellsDeps,
  ) {
    this.entity = entity;
    this.groupSelectStore = groupSelectStore;
    this.isGroupSelectOpen = entity.isGroupsDefaultOpened;
    this.trajectoryService = new TrajectoryService(new TrajectoriesApi(dashboardServiceAgent));

    this.referenceWellsService = referenceWells.referenceWellsService;
    this.referenceWellsStore = referenceWells.referenceWellsStore;
    this.referenceDataTemplatesStore = referenceWells.referenceDataTemplatesStore;
    this.referenceWellsChildrenStore = referenceWells.referenceWellsChildrenStore;
    this.referenceWellsService.setOnSelectionChange((tree) => {
      this.trajectoryService.onSelectedReferenceDataChange(tree);
    });
    // Push current selection (including hydrated initial tree) after the handler is wired.
    this.trajectoryService.onSelectedReferenceDataChange(this.referenceWellsService.getSelectedTreeForConsumers());

    entity.setDisposeHostStore(() => this.dispose());

    makeObservable(this);
  }

  getReferenceWellsSnapshot(): unknown {
    return this.referenceWellsStore.getSettingsSnapshot();
  }

  @computed
  get planOrFact(): TTrajectoryPlanOrFact {
    return this.entity.planOrFact;
  }

  @computed
  get wellId(): number | null {
    return this.entity.wellId;
  }

  @computed
  get wellboreId(): number | null {
    return this.entity.wellboreId;
  }

  @computed
  get trajectoryId(): number | null {
    return this.entity.trajectoryId;
  }

  @computed
  get wellboreOptions(): TReferenceWellboreCatalogItem[] {
    const wellId = this.entity.wellId;

    if (!hasValue(wellId)) {
      return [];
    }

    const catalog = this.referenceWellsStore.getWellChildren(wellId);

    if (!catalog) {
      return [];
    }

    if (this.entity.planOrFact === 'plan') {
      return catalog.planDesigns[0]?.wellbores ?? [];
    }

    return catalog.factWellbores;
  }

  @computed
  get trajectoryOptions(): TReferenceWellboreCatalogItem['trajectories'] {
    const wellboreId = this.entity.wellboreId;

    if (!hasValue(wellboreId)) {
      return [];
    }

    return this.wellboreOptions.find((item) => item.id === wellboreId)?.trajectories ?? [];
  }

  @computed
  get selectedDesignId(): number | null {
    if (this.entity.planOrFact !== 'plan' || !hasValue(this.entity.wellId)) {
      return null;
    }

    return this.referenceWellsStore.getWellChildren(this.entity.wellId)?.planDesigns[0]?.id ?? null;
  }

  @action.bound
  setGroupForTrajectory(group: TGroup | null): void {
    this.entity.setGroup(group ? group.id : null);
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  setReferenceWellsPanelOpen(isOpen: boolean): void {
    this.isReferenceWellsPanelOpen = isOpen;
  }

  @action.bound
  toggleReferenceWellsPanel(): void {
    this.isReferenceWellsPanelOpen = !this.isReferenceWellsPanelOpen;
  }

  @action.bound
  setStateParams(stateParams: TrajectoryWidgetStateParams): void {
    this.entity.setStateParams(stateParams);
  }

  @action.bound
  setPlanOrFact(planOrFact: TTrajectoryPlanOrFact): void {
    if (this.entity.planOrFact === planOrFact) {
      return;
    }

    this.entity.setPlanOrFact(planOrFact);
    this.resolveSelection({ forceAutoSelect: true });
  }

  @action.bound
  setWellboreId(wellboreId: number | null): void {
    if (this.entity.wellboreId === wellboreId) {
      return;
    }

    this.entity.setWellboreId(wellboreId);
    this.entity.setTrajectoryId(null);
    this.resolveSelection({ forceAutoSelectTrajectory: true });
  }

  @action.bound
  setTrajectoryId(trajectoryId: number | null): void {
    if (this.entity.trajectoryId === trajectoryId) {
      return;
    }

    this.entity.setTrajectoryId(trajectoryId);
    this.syncDataFromEntity();
  }

  private resolveSelection(options?: { forceAutoSelect?: boolean; forceAutoSelectTrajectory?: boolean }): void {
    const wellId = this.entity.wellId;

    if (!hasValue(wellId)) {
      this.entity.setWellboreId(null);
      this.entity.setTrajectoryId(null);
      this.syncDataFromEntity();
      return;
    }

    const wellbores = this.wellboreOptions;
    if (wellbores.length === 0) {
      this.syncDataFromEntity();
      return;
    }

    const savedWellboreId = this.entity.wellboreId;
    const wellboreValid =
      hasValue(savedWellboreId) && wellbores.some((item) => item.id === savedWellboreId) && !options?.forceAutoSelect;

    const resolvedWellbore = wellboreValid
      ? (wellbores.find((item) => item.id === savedWellboreId) ?? null)
      : pickActiveOrFirst(wellbores);

    const resolvedWellboreId = resolvedWellbore?.id ?? null;
    this.entity.setWellboreId(resolvedWellboreId);

    const trajectories = resolvedWellbore?.trajectories ?? [];
    if (trajectories.length === 0) {
      this.syncDataFromEntity();
      return;
    }

    const savedTrajectoryId = this.entity.trajectoryId;
    const trajectoryValid =
      hasValue(savedTrajectoryId) &&
      trajectories.some((item) => item.id === savedTrajectoryId) &&
      !options?.forceAutoSelect &&
      !options?.forceAutoSelectTrajectory;

    const resolvedTrajectory = trajectoryValid
      ? (trajectories.find((item) => item.id === savedTrajectoryId) ?? null)
      : pickActiveOrFirst(trajectories);

    this.entity.setTrajectoryId(resolvedTrajectory?.id ?? null);
    this.syncDataFromEntity();
  }

  private buildForcedSelection(): TForcedPrimarySelection {
    const { wellId, wellboreId, trajectoryId, planOrFact } = this.entity;

    if (!hasValue(wellId) || !hasValue(wellboreId)) {
      return null;
    }

    if (planOrFact === 'plan') {
      const designId = this.selectedDesignId;

      if (!hasValue(designId)) {
        return null;
      }

      return {
        wellId,
        branch: 'plan',
        designId,
        wellboreId,
        trajectoryId: trajectoryId ?? null,
      };
    }

    return {
      wellId,
      branch: 'fact',
      wellboreId,
      trajectoryId: trajectoryId ?? null,
    };
  }

  private syncForcedPrimarySelection(): void {
    this.referenceWellsStore.setForcedPrimarySelection(this.buildForcedSelection());
  }

  private syncDataFromEntity(): void {
    const { wellId, wellboreId, trajectoryId, planOrFact } = this.entity;

    this.trajectoryService.setContext({ wellId, planOrFact });

    if (!hasValue(wellId)) {
      this.trajectoryService.setWellbores(null);
      this.trajectoryService.setSelection({ wellboreId: null, trajectoryId: null });
      this.syncForcedPrimarySelection();
      return;
    }

    const wellbores = this.wellboreOptions.map((item) => {
      if (planOrFact !== 'fact') {
        return { id: item.id, name: item.name };
      }

      const factData = this.factWellboresCache.find((wellbore) => wellbore.id === item.id);

      return {
        id: item.id,
        name: item.name,
        parentWellboreId: factData?.parentWellboreId ?? null,
        mdKickoff: factData?.mdKickoff ?? null,
        referencePoints: factData?.referencePoints ?? [],
      };
    });
    this.trajectoryService.setWellbores(wellbores.length > 0 ? wellbores : null);
    this.trajectoryService.setSelection({
      wellboreId: wellboreId ?? null,
      trajectoryId: trajectoryId ?? null,
    });
    this.syncForcedPrimarySelection();
  }

  @action.bound
  private clearFactWellboresCache(): void {
    this.factWellboresAbortController?.abort();
    this.factWellboresAbortController = null;
    this.factWellboresCache = [];
  }

  @action.bound
  private setFactWellboresCache(items: TFetchedFactWellbore[]): void {
    this.factWellboresCache = items;
  }

  @flow.bound
  private async *loadFactWellbores(wellId: number) {
    this.factWellboresAbortController?.abort();
    const abortController = new AbortController();
    this.factWellboresAbortController = abortController;

    try {
      const { data } = yield wellDirectoryServiceAgent.get<TFetchedFactWellbore[]>(`wells/${wellId}/wellbores`, {
        signal: abortController.signal,
      });

      if (this.factWellboresAbortController !== abortController) {
        return;
      }

      this.setFactWellboresCache(Array.isArray(data) ? data : []);
      this.syncDataFromEntity();
    } catch (e) {
      yield;

      if (axios.isCancel(e) || this.factWellboresAbortController !== abortController) {
        return;
      }

      this.setFactWellboresCache([]);
      this.syncDataFromEntity();
    } finally {
      if (this.factWellboresAbortController === abortController) {
        this.factWellboresAbortController = null;
      }
    }
  }

  effect = () => {
    const disposeGroupId = reaction(
      () => this.entity.groupId,
      (groupId) => {
        if (groupId === null) return;
        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.entity, this.wellListPanel);
      },
    );

    // Init: load templates for the current well without touching selection
    // (tree may already be hydrated from GoStorage via initialSelectedTree).
    void this.referenceDataTemplatesStore.loadTemplatesForWell(this.entity.wellId);
    this.resolveSelection();

    // Real well changes only — no fireImmediately (init is not a well change).
    const disposeWellIdChange = reaction(
      () => this.entity.wellId,
      (wellId) => {
        this.referenceWellsService.setPrimaryWellId(wellId);
        void this.referenceDataTemplatesStore.reloadForWellChange(wellId);
        this.resolveSelection({ forceAutoSelect: true });
      },
    );

    // Re-resolve when catalog children become available / change.
    const disposeCatalog = reaction(
      () => {
        const wellId = this.entity.wellId;
        if (!hasValue(wellId)) {
          return null;
        }

        return this.referenceWellsStore.getWellChildren(wellId);
      },
      () => {
        this.resolveSelection();
      },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.store.selectedWellId,
      (wellId) => {
        if (this.wellListPanel.panelState?.groupId === this.entity.groupId) {
          this.entity.setWellId(wellId);
        }
      },
      { fireImmediately: true },
    );

    const disposeFactWellbores = reaction(
      () => ({
        wellId: this.entity.wellId,
        planOrFact: this.entity.planOrFact,
      }),
      ({ wellId, planOrFact }) => {
        if (!hasValue(wellId) || planOrFact !== 'fact') {
          this.clearFactWellboresCache();
          this.syncDataFromEntity();
          return;
        }

        void this.loadFactWellbores(wellId);
      },
      { fireImmediately: true },
    );

    this.effectDisposers = [
      disposeGroupId,
      disposeWellIdChange,
      disposeCatalog,
      wellListPanelDisposer,
      disposeFactWellbores,
    ];

    return () => {
      this.effectDisposers.forEach((d) => d());
      this.effectDisposers = [];
    };
  };

  @action.bound
  dispose(): void {
    this.clearFactWellboresCache();
    this.effectDisposers.forEach((d) => d());
    this.effectDisposers = [];
    this.referenceWellsChildrenStore.dispose();
    this.referenceWellsService.dispose();

    const storeService = requireService('widgetStoreService');
    storeService.removeStore(this.entity);
  }
}
